import React, { useEffect, useState } from 'react';
import { Shield, Bell, Heart, Lock, LogOut, ChevronRight, HelpCircle, Trash2, ShieldOff } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { apiService, formatApiError } from '../services/api';
import NotificationSettings from './NotificationSettings';
import { PremiumSection } from './Payments';
import ShareStoryCard from './ShareStoryCard';
import TeamInboxCard from './TeamInboxCard';

const Row = ({ icon: Icon, title, subtitle, onClick, testId, right }) => (
  <button onClick={onClick} className="w-full flex items-center justify-between py-3 text-left" data-testid={testId}>
    <div className="flex items-center space-x-3">
      <Icon className="w-5 h-5 text-gray-500" />
      <div><p className="font-medium text-gray-900">{title}</p>{subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}</div>
    </div>
    {right ?? <ChevronRight className="w-4 h-4 text-gray-400" />}
  </button>
);

const Settings = ({ onNavigate, onFootnoteClick }) => {
  const { toast } = useToast();
  const { logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [panel, setPanel] = useState(null); // 'notifications' | 'blocks' | 'password' | 'delete'
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => {
    apiService.users.getSettings().then((r) => setSettings(r.data.data || {})).catch((e) => toast({ title: 'Could not load settings', description: formatApiError(e), variant: 'destructive' }));
    apiService.safety.listBlocks().then((r) => setBlocks(r.data.data || [])).catch(() => {});
  }, []);

  const persist = async (patch) => {
    setSaving(true);
    try {
      const r = await apiService.users.updateSettings(patch);
      setSettings(r.data.data);
      toast({ title: 'Settings saved' });
    } catch (e) {
      toast({ title: 'Could not save', description: formatApiError(e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const unblock = async (userId) => {
    try { await apiService.safety.unblock(userId); setBlocks((b) => b.filter((x) => x.user_id !== userId)); } catch (e) { toast({ title: 'Failed', description: formatApiError(e), variant: 'destructive' }); }
  };

  const changePassword = async () => {
    try {
      await apiService.account.changePassword(pwForm);
      toast({ title: 'Password updated' });
      setPwForm({ current_password: '', new_password: '' }); setPanel(null);
    } catch (e) { toast({ title: 'Could not change password', description: formatApiError(e), variant: 'destructive' }); }
  };

  const deleteAccount = async () => {
    try {
      await apiService.account.deleteAccount({ password: deletePassword });
      toast({ title: 'Account deleted', description: 'Your personal data has been removed.' });
      await logout();
    } catch (e) { toast({ title: 'Could not delete account', description: formatApiError(e), variant: 'destructive' }); }
  };

  if (!settings) return <div className="text-center py-12 text-gray-500" data-testid="settings-loading">Loading settings…</div>;

  const ageRange = settings.age_range || [18, 80];
  const maxDistance = settings.max_distance ?? 25;

  return (
    <div className="space-y-6" data-testid="settings-screen">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

      <PremiumSection />

      <Card className="p-6 space-y-5">
        <h2 className="text-lg font-semibold flex items-center"><Heart className="w-5 h-5 mr-2 text-pink-500" />Discovery</h2>
        <div>
          <div className="flex justify-between text-sm mb-2"><span>Age range</span><span data-testid="settings-age-range">{ageRange[0]} – {ageRange[1]}</span></div>
          <Slider min={18} max={100} step={1} value={ageRange} onValueChange={(v) => setSettings({ ...settings, age_range: v })} onValueCommit={(v) => persist({ age_range: v })} data-testid="settings-age-slider" />
        </div>
        <div>
          <div className="flex justify-between text-sm mb-2"><span>Maximum distance</span><span data-testid="settings-max-distance">{maxDistance} mi</span></div>
          <Slider min={1} max={500} step={1} value={[maxDistance]} onValueChange={(v) => setSettings({ ...settings, max_distance: v[0] })} onValueCommit={(v) => persist({ max_distance: v[0] })} data-testid="settings-distance-slider" />
        </div>
        <div className="flex items-center justify-between">
          <div><p className="font-medium">Show me on FlameLink</p><p className="text-xs text-gray-500">Turn off to hide your profile from discovery</p></div>
          <Switch checked={settings.show_me_on_tinder !== false} onCheckedChange={(v) => persist({ show_me_on_tinder: v })} disabled={saving} data-testid="settings-show-me-switch" />
        </div>
      </Card>

      <Card className="p-6 divide-y">
        <Row icon={Bell} title="Notifications" subtitle="Email and in-app alerts" onClick={() => setPanel(panel === 'notifications' ? null : 'notifications')} testId="settings-notifications-row" />
        {panel === 'notifications' && <div className="py-3"><NotificationSettings /></div>}
        <Row icon={ShieldOff} title="Blocked users" subtitle={`${blocks.length} blocked`} onClick={() => setPanel(panel === 'blocks' ? null : 'blocks')} testId="settings-blocks-row" />
        {panel === 'blocks' && (
          <div className="py-3 space-y-2" data-testid="settings-blocks-panel">
            {blocks.length === 0 && <p className="text-sm text-gray-500">You haven't blocked anyone.</p>}
            {blocks.map((b) => (
              <div key={b.user_id} className="flex items-center justify-between text-sm"><span>{b.name}</span><Button size="sm" variant="outline" onClick={() => unblock(b.user_id)} data-testid={`unblock-${b.user_id}`}>Unblock</Button></div>
            ))}
          </div>
        )}
        <Row icon={Lock} title="Change password" onClick={() => setPanel(panel === 'password' ? null : 'password')} testId="settings-password-row" />
        {panel === 'password' && (
          <div className="py-3 space-y-2" data-testid="settings-password-panel">
            <Input type="password" placeholder="Current password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="current-password-input" />
            <Input type="password" placeholder="New password (8+ chars, letters & numbers)" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="new-password-input" />
            <Button onClick={changePassword} disabled={!pwForm.current_password || pwForm.new_password.length < 8} data-testid="change-password-submit">Update password</Button>
          </div>
        )}
      </Card>

      <Card className="p-6 divide-y">
        <Row icon={Shield} title="Safety tips" onClick={() => onFootnoteClick?.('safety')} testId="settings-safety-row" />
        <Row icon={HelpCircle} title="Help center" onClick={() => onFootnoteClick?.('help')} testId="settings-help-row" />
        <Row icon={HelpCircle} title="Privacy policy" onClick={() => onFootnoteClick?.('privacy')} testId="settings-privacy-row" />
      </Card>

      <TeamInboxCard />

      <ShareStoryCard />

      <Card className="p-6 space-y-3">
        <Button variant="outline" className="w-full" onClick={logout} data-testid="settings-logout-btn"><LogOut className="w-4 h-4 mr-2" />Log out</Button>
        <Button variant="outline" className="w-full text-red-600 border-red-200" onClick={() => setPanel(panel === 'delete' ? null : 'delete')} data-testid="settings-delete-account-btn"><Trash2 className="w-4 h-4 mr-2" />Delete my account</Button>
        {panel === 'delete' && (
          <div className="space-y-2 p-3 bg-red-50 rounded-lg" data-testid="settings-delete-panel">
            <p className="text-sm text-red-700">This permanently removes your profile, photos, matches, messages and swipes. Enter your password to confirm.</p>
            <Input type="password" placeholder="Your password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} data-testid="delete-password-input" />
            <Button className="w-full bg-red-600 hover:bg-red-700 text-white" disabled={!deletePassword} onClick={deleteAccount} data-testid="delete-account-confirm-btn">Permanently delete my account</Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Settings;
